// src/pages/Register.tsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../components/Auth.css";

// =====================================================
// CONFIGURATION
// =====================================================

const MINIMUM_AGE = 18;

import API_URL from "../config/api";

import {
  trackAnalyticsEvent,
} from "../services/analytics";

// =====================================================
// TYPES
// =====================================================

interface RegisterResponse {
  success: boolean;
  message?: string;

  user?: {
    id: string | number;
    fullName: string;
    username: string;
    email: string;
    emailVerified: boolean;
    createdAt?: string;
  };

  code?: string;
}

// =====================================================
// COMPONENT
// =====================================================

const Register: React.FC = () => {
  const navigate = useNavigate();

  // ===================================================
  // FORM STATE
  // ===================================================

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] =
    useState("");

  // ===================================================
  // UI STATE
  // ===================================================

  const [showPassword, setShowPassword] =
    useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  // ===================================================
  // VALIDATION RULES
  // ===================================================

  const emailRegex =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const usernameRegex =
    /^[a-zA-Z0-9_]{3,20}$/;

  // ===================================================
  // AGE CALCULATION
  // ===================================================

  const getAge = (
    dob: string
  ): number | null => {
    if (!dob) {
      return null;
    }

    // Expected format:
    // YYYY-MM-DD

    const parts = dob.split("-");

    if (parts.length !== 3) {
      return null;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(month) ||
      !Number.isInteger(day)
    ) {
      return null;
    }

    const birthDate = new Date(
      year,
      month - 1,
      day
    );

    // Reject impossible dates
    if (
      birthDate.getFullYear() !== year ||
      birthDate.getMonth() !== month - 1 ||
      birthDate.getDate() !== day
    ) {
      return null;
    }

    const today = new Date();

    // Future DOB
    if (birthDate > today) {
      return null;
    }

    let age =
      today.getFullYear() -
      birthDate.getFullYear();

    const monthDifference =
      today.getMonth() -
      birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (
        monthDifference === 0 &&
        today.getDate() <
          birthDate.getDate()
      )
    ) {
      age--;
    }

    return age;
  };

  const age = getAge(dateOfBirth);

  // ===================================================
  // DERIVED VALIDATION
  // ===================================================

  const hasValidName =
    name.trim().length >= 2 &&
    name.trim().length <= 100;

  const hasValidUsername =
    usernameRegex.test(
      username.trim()
    );

  const hasValidEmail =
    emailRegex.test(
      email.trim()
    );

  const hasValidDOB =
    dateOfBirth !== "" &&
    age !== null &&
    age >= 0;

  const isOldEnough =
    age !== null &&
    age >= MINIMUM_AGE;

  const hasMinimumPasswordLength =
    password.length >= 8;

  const hasValidPasswordLength =
    password.length >= 8 &&
    password.length <= 128;

  const passwordsMatch =
    password === confirmPassword &&
    confirmPassword.length > 0;

  const isValid =
    hasValidName &&
    hasValidUsername &&
    hasValidEmail &&
    hasValidDOB &&
    isOldEnough &&
    hasValidPasswordLength &&
    passwordsMatch;

  // ===================================================
  // MAX DOB
  // Prevent future dates.
  // ===================================================

  const now = new Date();

  const todayString = [
    now.getFullYear(),
    String(
      now.getMonth() + 1
    ).padStart(2, "0"),
    String(
      now.getDate()
    ).padStart(2, "0"),
  ].join("-");

  // ===================================================
  // REGISTER
  // ===================================================

  const handleRegister = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    setError("");

    // =================================================
    // NAME VALIDATION
    // =================================================

    if (!hasValidName) {
      setError(
        "Please enter a valid full name."
      );

      return;
    }

    // =================================================
    // USERNAME VALIDATION
    // =================================================

    if (!hasValidUsername) {
      setError(
        "Username must be 3-20 characters and contain only letters, numbers, or underscores."
      );

      return;
    }

    // =================================================
    // EMAIL VALIDATION
    // =================================================

    if (!hasValidEmail) {
      setError(
        "Please enter a valid email address."
      );

      return;
    }

    // =================================================
    // DOB VALIDATION
    // =================================================

    if (!dateOfBirth) {
      setError(
        "Please enter your date of birth."
      );

      return;
    }

    if (
      age === null ||
      age < 0
    ) {
      setError(
        "Please enter a valid date of birth."
      );

      return;
    }

    // =================================================
    // AGE REQUIREMENT
    // =================================================

    if (!isOldEnough) {
      setError(
        `You must be at least ${MINIMUM_AGE} years old to create a P-ARENA account.`
      );

      return;
    }

    // =================================================
    // PASSWORD VALIDATION
    // =================================================

    if (!hasMinimumPasswordLength) {
      setError(
        "Password must contain at least 8 characters."
      );

      return;
    }

    if (password.length > 128) {
      setError(
        "Password cannot exceed 128 characters."
      );

      return;
    }

    // =================================================
    // CONFIRM PASSWORD
    // =================================================

    if (!passwordsMatch) {
      setError(
        "Passwords do not match."
      );

      return;
    }

    try {
      setLoading(true);

      // =================================================
      // REGISTRATION DATA
      // =================================================

      const registrationData = {
        name: name.trim(),

        username:
          username
            .trim()
            .toLowerCase(),

        email:
          email
            .trim()
            .toLowerCase(),

        dateOfBirth,

        password,
      };

      // =================================================
      // CALL REAL BACKEND
      // POST /api/auth/register
      // =================================================

      const response = await fetch(
        `${API_URL}/api/auth/register`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify(
            registrationData
          ),
        }
      );

      // =================================================
      // READ BACKEND RESPONSE
      // =================================================

      let data: RegisterResponse;

      try {
        data =
          (await response.json()) as RegisterResponse;
      } catch {
        throw new Error(
          "The server returned an invalid response."
        );
      }

      // =================================================
      // BACKEND ERROR
      // =================================================

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to create account."
        );
      }

      // =================================================
      // VERIFY SUCCESS
      // =================================================

      if (!data.success) {
        throw new Error(
          data.message ||
            "Unable to create account."
        );
      }

      console.log(
        "✅ Account created:",
        data.user?.username
      );

      // =================================================
      // ANALYTICS — SUCCESSFUL REGISTRATION
      // =================================================
      //
      // Registration does not automatically log the
      // player in. Therefore this event is associated
      // with the current anonymous visitor/session.
      //
      // After the player logs in, the separate login
      // event will upgrade the session to the
      // authenticated P-ARENA user.
      //

      void trackAnalyticsEvent(
        "registration",
        {
          pagePath: "/register",
        }
      );

      // =================================================
      // SUCCESS → LOGIN
      // =================================================

      navigate("/login", {
        replace: true,

        state: {
          registered: true,

          username:
            data.user?.username,
        },
      });

    } catch (err) {
      console.error(
        "❌ Registration error:",
        err
      );

      if (err instanceof TypeError) {
        setError(
          "Unable to connect to the P-ARENA server. Please make sure the backend is running."
        );

        return;
      }

      if (err instanceof Error) {
        setError(err.message);

        return;
      }

      setError(
        "We couldn't create your account. Please try again."
      );

    } finally {
      setLoading(false);
    }
  };

  // ===================================================
  // RETURN
  // ===================================================

  return (
    <div className="app-background auth-container">

      {/* ============================================= */}
      {/* BACK TO GAME HOUSE                            */}
      {/* ============================================= */}

      <button
        type="button"
        className="back-home"
        disabled={loading}
        onClick={() =>
          navigate("/")
        }
      >
        ← Game House
      </button>

      {/* ============================================= */}
      {/* REGISTER CARD                                 */}
      {/* ============================================= */}

      <div className="auth-box">

        {/* =========================================== */}
        {/* HEADING                                     */}
        {/* =========================================== */}

        <div className="auth-heading">

          <div className="auth-logo">
            ♠ P-ARENA
          </div>

          <h2>
            Create Account
          </h2>

          <p>
            Join P-ARENA and play with friends online.
          </p>

        </div>

        {/* =========================================== */}
        {/* FORM                                        */}
        {/* =========================================== */}

        <form
          onSubmit={handleRegister}
          noValidate
        >

          {/* ========================================= */}
          {/* FULL NAME                                 */}
          {/* ========================================= */}

          <div className="input-group">

            <label htmlFor="register-name">
              Full Name
            </label>

            <input
              id="register-name"
              type="text"
              placeholder="Enter your full name"
              value={name}
              autoComplete="name"
              maxLength={100}
              disabled={loading}
              onChange={(event) => {
                setName(
                  event.target.value
                );

                setError("");
              }}
            />

          </div>

          {/* ========================================= */}
          {/* USERNAME                                  */}
          {/* ========================================= */}

          <div className="input-group">

            <label htmlFor="register-username">
              Username
            </label>

            <input
              id="register-username"
              type="text"
              placeholder="Choose a username"
              value={username}
              autoComplete="username"
              minLength={3}
              maxLength={20}
              disabled={loading}
              onChange={(event) => {
                const value =
                  event.target.value
                    .replace(/\s/g, "");

                setUsername(value);

                setError("");
              }}
            />

            <small className="input-help">
              3-20 characters. Letters,
              numbers and underscores.
            </small>

          </div>

          {/* ========================================= */}
          {/* EMAIL                                     */}
          {/* ========================================= */}

          <div className="input-group">

            <label htmlFor="register-email">
              Email Address
            </label>

            <input
              id="register-email"
              type="email"
              placeholder="Enter your email address"
              value={email}
              autoComplete="email"
              maxLength={255}
              disabled={loading}
              onChange={(event) => {
                setEmail(
                  event.target.value
                );

                setError("");
              }}
            />

          </div>

          {/* ========================================= */}
          {/* DATE OF BIRTH                             */}
          {/* ========================================= */}

          <div className="input-group">

            <label htmlFor="register-dob">
              Date of Birth
            </label>

            <input
              id="register-dob"
              type="date"
              value={dateOfBirth}
              autoComplete="bday"
              max={todayString}
              disabled={loading}
              onChange={(event) => {
                setDateOfBirth(
                  event.target.value
                );

                setError("");
              }}
            />

            <small className="input-help">
              You must be at least{" "}
              {MINIMUM_AGE} years old to
              create an account.
            </small>

            {dateOfBirth &&
              age !== null &&
              age >= 0 &&
              !isOldEnough && (
                <div className="field-error">
                  You must be at least{" "}
                  {MINIMUM_AGE} years old
                  to play P-ARENA.
                </div>
              )}

          </div>

          {/* ========================================= */}
          {/* PASSWORD                                  */}
          {/* ========================================= */}

          <div className="input-group">

            <label htmlFor="register-password">
              Password
            </label>

            <div className="password-input-wrapper">

              <input
                id="register-password"
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                placeholder="Create a password"
                value={password}
                autoComplete="new-password"
                maxLength={128}
                disabled={loading}
                onChange={(event) => {
                  setPassword(
                    event.target.value
                  );

                  setError("");
                }}
              />

              <button
                type="button"
                className="password-toggle"
                aria-label={
                  showPassword
                    ? "Hide password"
                    : "Show password"
                }
                aria-pressed={
                  showPassword
                }
                disabled={loading}
                onClick={() =>
                  setShowPassword(
                    (current) =>
                      !current
                  )
                }
              >
                {showPassword
                  ? "🙈"
                  : "👁"}
              </button>

            </div>

            <small className="input-help">
              Use at least 8 characters.
            </small>

          </div>

          {/* ========================================= */}
          {/* CONFIRM PASSWORD                          */}
          {/* ========================================= */}

          <div className="input-group">

            <label htmlFor="register-confirm-password">
              Confirm Password
            </label>

            <div className="password-input-wrapper">

              <input
                id="register-confirm-password"
                type={
                  showConfirmPassword
                    ? "text"
                    : "password"
                }
                placeholder="Enter your password again"
                value={confirmPassword}
                autoComplete="new-password"
                maxLength={128}
                disabled={loading}
                onChange={(event) => {
                  setConfirmPassword(
                    event.target.value
                  );

                  setError("");
                }}
              />

              <button
                type="button"
                className="password-toggle"
                aria-label={
                  showConfirmPassword
                    ? "Hide password"
                    : "Show password"
                }
                aria-pressed={
                  showConfirmPassword
                }
                disabled={loading}
                onClick={() =>
                  setShowConfirmPassword(
                    (current) =>
                      !current
                  )
                }
              >
                {showConfirmPassword
                  ? "🙈"
                  : "👁"}
              </button>

            </div>

            {confirmPassword &&
              password !==
                confirmPassword && (
                <div className="field-error">
                  Passwords do not match.
                </div>
              )}

          </div>

          {/* ========================================= */}
          {/* GENERAL ERROR                             */}
          {/* ========================================= */}

          {error && (
            <div
              className="error"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* ========================================= */}
          {/* CREATE ACCOUNT                            */}
          {/* ========================================= */}

          <button
            type="submit"
            className="auth-submit-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="button-spinner"
                  aria-hidden="true"
                />

                Creating Account...
              </>
            ) : (
              "Create Account"
            )}
          </button>

        </form>

        {/* =========================================== */}
        {/* LOGIN LINK                                  */}
        {/* =========================================== */}

        <div className="auth-footer">

          <span>
            Already have an account?
          </span>

          <button
            type="button"
            className="auth-link-button"
            disabled={loading}
            onClick={() =>
              navigate("/login")
            }
          >
            Login
          </button>

        </div>

      </div>

    </div>
  );
};

export default Register;